import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

// Social Proof: credentials, founder bio, two summit-stage video embeds.
// Placed between Hero and Scorecard (32px above, 48px below).

const VIDEOS = [
  {
    id: "vid1",
    videoId: "7zcy2roYATo",
    src: "https://www.youtube.com/embed/7zcy2roYATo?start=1580&end=1630",
    fallbackUrl: "https://www.youtube.com/watch?v=7zcy2roYATo&t=1580s",
    title: "First-party data & signal engineering — clip 1",
  },
  {
    id: "vid2",
    videoId: "NTdDTSjYB5M",
    src: "https://www.youtube.com/embed/NTdDTSjYB5M?start=630&end=670",
    fallbackUrl: "https://www.youtube.com/watch?v=NTdDTSjYB5M&t=630s",
    title: "First-party data & signal engineering — clip 2",
  },
];

function VideoEmbed({ video }) {
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [posterLoaded, setPosterLoaded] = useState(false);
  const [hover, setHover] = useState(false);
  const [posterHover, setPosterHover] = useState(false);

  // Safety timeout: if the poster never resolves within 6s, assume the
  // video is unreachable and show the fallback instead.
  useEffect(() => {
    if (posterLoaded || failed) return undefined;
    const t = setTimeout(() => {
      if (!posterLoaded) setFailed(true);
    }, 6000);
    return () => clearTimeout(t);
  }, [posterLoaded, failed]);

  const iframeSrc = playing
    ? `${video.src.replace("www.youtube.com", "www.youtube-nocookie.com")}${
        video.src.includes("?") ? "&" : "?"
      }autoplay=1&mute=1&playsinline=1&rel=0`
    : video.src;

  return (
    <div
      data-testid={`social-proof-video-${video.id}`}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 480,
        paddingBottom: "56.25%",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid var(--rtd-border)",
        margin: "0 auto",
      }}
    >
      {/* Poster (click-to-play). Shown until user clicks or a failure occurs. */}
      {!failed && !playing && (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          onMouseEnter={() => setPosterHover(true)}
          onMouseLeave={() => setPosterHover(false)}
          aria-label={`Play video: ${video.title}`}
          data-testid={`social-proof-video-${video.id}-poster`}
          style={{
            position: "absolute",
            inset: 0,
            padding: 0,
            border: 0,
            background: "#0B1929",
            cursor: "pointer",
            overflow: "hidden",
          }}
        >
          <img
            src={`https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`}
            alt=""
            onLoad={() => setPosterLoaded(true)}
            onError={() => setFailed(true)}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: posterLoaded ? 1 : 0,
              transform: posterHover ? "scale(1.02)" : "scale(1)",
              transition: "opacity 300ms ease, transform 400ms ease",
            }}
          />
          {/* Play chip */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: `translate(-50%, -50%) scale(${posterHover ? 1.06 : 1})`,
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "var(--rtd-gold)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
              transition: "transform 200ms ease, box-shadow 200ms ease",
            }}
          >
            <svg width="22" height="26" viewBox="0 0 22 26" aria-hidden="true">
              <path d="M2 2 L20 13 L2 24 Z" fill="var(--rtd-navy)" />
            </svg>
          </span>
          {/* subtle bottom shade for legibility if we ever add a title chip */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.35) 100%)",
              pointerEvents: "none",
            }}
          />
        </button>
      )}

      {!failed && playing && (
        <iframe
          src={iframeSrc}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          onError={() => setFailed(true)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
        />
      )}

      {failed && (
        <div
          data-testid={`social-proof-video-${video.id}-fallback`}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            backgroundColor: "#F9F6F1",
            border: "1px solid #E5E7EB",
            borderRadius: 12,
          }}
        >
          <p
            className="font-body"
            style={{
              fontSize: 14,
              color: "#6B7280",
              textAlign: "center",
              marginBottom: 8,
              lineHeight: 1.5,
            }}
          >
            CustomerLabs First Party Data Ops Summit
          </p>
          <a
            href={video.fallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className="font-body"
            style={{
              fontSize: 14,
              color: "#E8A020",
              fontWeight: 700,
              textAlign: "center",
              textDecoration: "none",
              opacity: hover ? 0.8 : 1,
              transition: "opacity 150ms ease",
            }}
          >
            Watch on YouTube →
          </a>
        </div>
      )}
    </div>
  );
}

export default function SocialProof() {
  return (
    <section
      className="w-full"
      data-testid="social-proof-section"
      style={{
        backgroundColor: "var(--rtd-bg)",
        padding: "80px 24px",
        marginTop: 24,
        marginBottom: 48,
      }}
    >
      <div className="mx-auto" style={{ maxWidth: 680 }}>
        {/* Credential block */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55 }}
          className="mx-auto text-center"
          style={{ maxWidth: 560 }}
        >
          <h2
            className="font-serif-display"
            style={{ fontSize: 22, color: "var(--rtd-text)", lineHeight: 1.3 }}
            data-testid="social-proof-credential-headline"
          >
            Speaker at India&rsquo;s Only First Party Data Ops Summit &mdash; CustomerLabs
          </h2>
          <p
            className="font-body"
            style={{ fontSize: 14, color: "var(--rtd-text-secondary)", marginTop: 8 }}
          >
            Delhi and Bangalore editions
          </p>
        </motion.div>

        {/* Founder bio block */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, delay: 0.08 }}
          className="mx-auto text-center"
          style={{ marginTop: 24, maxWidth: 560 }}
        >
          <div
            className="font-serif-display"
            style={{ fontSize: 18, color: "var(--rtd-text)", lineHeight: 1.3 }}
            data-testid="social-proof-founder-name"
          >
            Shubhro
          </div>
          <div
            className="font-body"
            style={{ fontSize: 14, color: "var(--rtd-text-secondary)", marginTop: 4 }}
          >
            Co-founder, Right to Digital
          </div>
          <p
            className="font-body mx-auto"
            style={{
              fontSize: 13,
              color: "var(--rtd-text-secondary)",
              fontStyle: "italic",
              lineHeight: 1.6,
              marginTop: 12,
              maxWidth: 480,
            }}
            data-testid="social-proof-founder-bio"
          >
            Practitioner in first party data and signal engineering. Helping health and
            wellness brands train their ad algorithms on revenue, not form fills.
          </p>
          <a
            href="https://www.linkedin.com/in/shubhroniltalukdar/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="social-proof-linkedin-link"
            className="font-body"
            style={{
              display: "block",
              marginTop: 8,
              fontSize: 13,
              color: "#E8A020",
              textAlign: "center",
              textDecoration: "none",
            }}
          >
            Connect on LinkedIn →
          </a>
        </motion.div>

        {/* Video headline */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, delay: 0.14 }}
          className="text-center"
          style={{ marginTop: 24 }}
        >
          <h3
            className="font-serif-display"
            style={{ fontSize: 20, color: "var(--rtd-text)" }}
            data-testid="social-proof-video-headline"
          >
            From the Summit Stage
          </h3>
        </motion.div>

        {/* Videos: 2 columns on desktop (>=768px), stacked on mobile.
            Rendered OUTSIDE the 680px content wrapper so the grid can widen to 900px on desktop. */}
      </div>

      <div style={{ marginTop: 16 }}>
        <div
          className="grid grid-cols-1 md:grid-cols-2 mx-auto max-w-[480px] md:max-w-[900px]"
          style={{ gap: 16 }}
          data-testid="social-proof-video-grid"
        >
          {VIDEOS.map((v) => (
            <VideoEmbed key={v.id} video={v} />
          ))}
        </div>
      </div>

      <div className="mx-auto" style={{ maxWidth: 680 }}>
        <p
          className="font-body text-center"
          style={{
            marginTop: 12,
            fontSize: 13,
            color: "var(--rtd-muted)",
            fontStyle: "italic",
          }}
          data-testid="social-proof-video-caption"
        >
          On first party data and signal engineering in performance marketing
        </p>
      </div>
    </section>
  );
}
